import { useCallback, useState } from 'react';
import { DateTime } from 'luxon';
import { LOCAL_STORAGE_KEYS } from 'src/constants/local-storage';
import { getLocalStorageValue, setLocalStorageValue } from 'src/utils/local-storage';

const DISMISSAL_CADENCE_DAYS = 7;

type StoredDismissal = {
  readonly dismissedAtMillis: number;
  readonly disconnectedIds: string;
};

function isStoredDismissal(value: unknown): value is StoredDismissal {
  if (typeof value !== 'object' || value === null) return false;
  if (!('dismissedAtMillis' in value) || !('disconnectedIds' in value)) return false;
  const record: Record<string, unknown> = value;
  return typeof record['dismissedAtMillis'] === 'number' && typeof record['disconnectedIds'] === 'string';
}

export function useDisconnectedBannerDismissal(currentDisconnectedIds: string): {
  readonly isDismissed: boolean;
  readonly dismiss: () => void;
} {
  const [stored, setStored] = useState<StoredDismissal | null>(() =>
    typeof window === 'undefined'
      ? null
      : getLocalStorageValue<StoredDismissal>({
          dataKey: LOCAL_STORAGE_KEYS.DISCONNECTED_ACCOUNTS_BANNER_DISMISSAL,
          validator: isStoredDismissal,
        }),
  );

  const dismiss = useCallback(() => {
    const value: StoredDismissal = {
      dismissedAtMillis: DateTime.now().toMillis(),
      disconnectedIds: currentDisconnectedIds,
    };
    setLocalStorageValue({
      dataKey: LOCAL_STORAGE_KEYS.DISCONNECTED_ACCOUNTS_BANNER_DISMISSAL,
      data: value,
      validator: isStoredDismissal,
    });
    setStored(value);
  }, [currentDisconnectedIds]);

  const isDismissed =
    stored !== null &&
    stored.disconnectedIds === currentDisconnectedIds &&
    DateTime.now().diff(DateTime.fromMillis(stored.dismissedAtMillis), 'days').days < DISMISSAL_CADENCE_DAYS;

  return { isDismissed, dismiss };
}
